import { Menu, Dropdown, notification, Modal } from 'antd';
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { MenuOutlined } from '@ant-design/icons';
import PortfolioRuleEngine from "../../RuleEngine";
import { usePortfolio } from "../../../../Contexts/PortfolioContext";
import { useBuilder } from "../../../../Contexts/BuilderContext";
import { fetchDetailedPortfolioObjects } from "../../../../Api/portfolioApi";
import PersonalizePortfolioForm from "../../PersonalizePortfolioForm";
import useClipboard from "../../../../Hooks/useClipboard";
import {setTickerGroup} from "../../../../Store/slices/appState.slice";
import {useGptResponses} from "../../../../Hooks/useGptResponses";
import ResponseMessage from "../../../../Components/ResponseMessage";


const extractSectors = (sectorsObj)=>{
    const sectorsArray = Object.keys(sectorsObj).map((key)=>{return {name: key, value: sectorsObj[key]}});
    sectorsArray.sort((a, b)=>b.value-a.value);
    return sectorsArray;
}

const extractPortfolioDataForPrompt = (portfolio) => {
    const sectors = extractSectors(portfolio.tags_v2?.sector_gic);
    let promptData = 'Portfolio Data: \n';
    promptData += `Div Yield: ${portfolio?.tags_v2?.metrics.dy}; PE (TTM) ${portfolio.tags_v2?.metrics.pe_ttm}`;
    promptData += `\nSectors: ${sectors.map(s=>`${s.name} (${s.value})`).join(', ')}`;
    return promptData
}


const PROMPT_HEAD = `Describe the portfolio based on the data provided. Highlight how the portfolio is positioned for 
modern market conditions. Ensure no negative remarks or insights are made on the portfolio, and really bridge how the
current positioning is beneficial based on current market and geopolitical environment`;


/**
 *
 * @param onEdit {function} - Function to handle the edit action
 * @param portfolio {object} - Portfolio object document
 * @returns {JSX.Element}
 * @constructor
 */
const TableRowActions = ({ onEdit, portfolio }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = localStorage.getItem('uid');
    const { setExistingPortfolioBuilderContext } = useBuilder();
    const { selectSmaPortfolio } = usePortfolio(); // Use the new setter for SMA Portfolio
    const [currentAction, setCurrentAction] = useState(null);
    const [fetchComplete, setFetchComplete] = useState(false);
    const [fetchedPortfolioId, setFetchedPortfolioId] = useState(null);
    const [personalizationModalVisible, setPersonalizationModalVisible] = useState(false);
    const [showGptSummary, setShowGptSummary] = useState(false);
    const copyToClipboard = useClipboard();
    const { responseId, submitPrompt } = useGptResponses();
    const gptResponse = useSelector(state => state.gptApi.responses[responseId]);

    console.log('gptResponse', gptResponse);

    const updatedPortfolio = useSelector(state =>
        state.portfolios.items.find(p => p.id === fetchedPortfolioId)
    );
    const portfolioUserId = portfolio?.attr?.create_uid;

    const handleSmaAfterFetch = ()=>{
        let smaConfig = {symbols: updatedPortfolio.symbols, params: {}, portfolio: {}, linkedPortfolio: portfolio}
            if (fetchedPortfolioId !== portfolio.id) {
                smaConfig.params = portfolio.properties.sma_config;
                smaConfig.portfolio = portfolio;
                smaConfig.linkedPortfolio = updatedPortfolio;
            }
            if (updatedPortfolio && updatedPortfolio.symbols) {
                selectSmaPortfolio(smaConfig);
                navigate('/builders/sma');
            }
    }
    const handleUpdatePositionsAfterFetch = ()=>{
        setExistingPortfolioBuilderContext(updatedPortfolio);
        navigate('/builders/portfolio');
    }
    const handleFactSheet = ()=>{
        navigate(`/portfolio/${portfolio.id}/factsheet`);
    }

    useEffect(() => {
        if (fetchComplete) {
            setFetchComplete(false); // Reset the fetchComplete flag
            if (currentAction==='sma'){
                handleSmaAfterFetch();
            }
            else if (currentAction==='positions') {
                handleUpdatePositionsAfterFetch();
            }
            else if (currentAction === 'explain') {
                const promptString = extractPortfolioDataForPrompt(updatedPortfolio);
                submitPrompt(`${PROMPT_HEAD}\r\n${promptString}`);
                setShowGptSummary(true);

                // handlePortfolioPrompt(updatedPortfolio);
            }
        }
    }, [fetchComplete, updatedPortfolio, navigate, selectSmaPortfolio]);

    const _fetchAndHandlePortfolioAction = (portfolioId) => {
        dispatch(fetchDetailedPortfolioObjects([portfolioId])).then(() => {
            setFetchComplete(true);
        });
    };

    const handleSMA = (pid)=>{
        setCurrentAction('sma');
        setFetchedPortfolioId(pid);
        _fetchAndHandlePortfolioAction(pid);
    }


    const updatePortfolioPositions = ()=>{
        setCurrentAction('positions');
        setFetchedPortfolioId(portfolio.id)
        _fetchAndHandlePortfolioAction(portfolio.id, (payload)=>{
            setExistingPortfolioBuilderContext(payload);
            navigate('/builders/portfolio');
        })
    }


    const handleViewTickers = ()=>{
        _fetchAndHandlePortfolioAction(portfolio.id, (payload)=>{
            dispatch(setTickerGroup(payload.symbols));
            navigate('/beta/tickers/display');
        });
    }

    const handleSubmitPersonalizedPortfolio = (formValues)=>{
        const url = `/public/portfolio/${portfolio.id}?clientName=${formValues.clientName}&riskScore=${formValues.riskScore}`;
        navigate(url);
    }

    const handleExplainPortfolio = (portfolioId) => {
      setCurrentAction('explain');
      setFetchedPortfolioId(portfolioId);
      _fetchAndHandlePortfolioAction(portfolioId);
    };

    let rules = new PortfolioRuleEngine(portfolio);
    let isSMA = rules.isSMA();

    let menuItems = [
        {
            key: 'open',
            label: 'Open in New Tab',
            onClick: ()=>window.open(`/public/portfolio/${portfolio.id}`)
        },
        {
            key: 'view',
            label: 'View in Page',
            onClick: ()=>navigate(`/portfolio/${portfolio.id}`)

        },
        {
            key: 'copy',
            label: "Copy ID",
            onClick: ()=>copyToClipboard(portfolio.id)
        },
        {
            key: "more",
            label: "More Options",
            children: [
                {
                    key: 'viewSymbols',
                    label: 'View Tickers',
                    onClick: ()=>handleViewTickers()
                },
            ]
        },
        {
            key: 'positions',
            label: 'Signal History',
            onClick: ()=>navigate(`/signals/${portfolio.id}/history`)
        },
        {
            key: 'sma',
            label: isSMA ? 'Edit SMA' : 'Create SMA',
            onClick: ()=>handleSMA(isSMA?portfolio.properties.linked_portfolio.id:portfolio.id)
        },
        {
          key: 'explain',
          label: 'Explain Portfolio',
          onClick: () => handleExplainPortfolio(portfolio.id)
        },
        {
            key: 'factSheet',
            label: 'Fact Sheet',
            onClick: ()=>handleFactSheet()
        }
    ];

    if (portfolioUserId && portfolioUserId===userId){
        menuItems.push({
            key: "edits",
            label: "Edit",
            children: [
                {
                    key: 'editInfo',
                    label: 'Edit Info',
                    onClick: onEdit
                },
                {
                    key: 'updatePositions',
                    label: 'Update Positions',
                    onClick: updatePortfolioPositions
                }
            ]
        });
    }

    return (
        <>
            <Dropdown overlay={<Menu theme={'dark'} items={menuItems}></Menu>}
                      trigger={['hover']}
            >
                <MenuOutlined style={{fontSize: '18px'}} />
            </Dropdown>
            {/* Modal for personalized portfolio form plugin */}
            <Modal
              title="Personalize Portfolio"
              visible={personalizationModalVisible}
              onCancel={() => setPersonalizationModalVisible(false)}
              footer={null}
            >
                <div className={'p-3'}>
                    <PersonalizePortfolioForm onSubmit={(formvalues)=>handleSubmitPersonalizedPortfolio(formvalues)} />
                </div>
            </Modal>
            <Modal open={showGptSummary} onCancel={()=>setShowGptSummary(false)}>
                {gptResponse?.data && <ResponseMessage response={gptResponse} />}
            </Modal>
        </>
    );
};

export default TableRowActions;
